import Domain from "@/models/Domain.js";
import { useRepo } from "pinia-orm";
import { requestFactory } from "@socotec.io/socio-vue-components";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { list, listAll } from "@/utils/factoryRest";
import { DomainApi, PublicDomainApi } from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";
import { defineStore } from "pinia";

const domainApi = new DomainApi(configs.certipersBackConfig);
const publicDomainApi = new PublicDomainApi(configs.certipersBackConfig);
const DomainRepo = useRepo(Domain);

const formatDomain = (domain) => {
  const domainGroupNested = typeof domain.domainGroup === "object";
  return {
    uuid: domain.uuid,
    name: domain.name,
    createdAt: domain.createdAt,
    updatedAt: domain.updatedAt,
    code: domain.code,
    nameWithoutMention: domain.nameWithoutMention,
    nameWithMention: domain.nameWithMention,
    isCofrac: domain.isCofrac,
    domainGroupUuid: domainGroupNested ? domain.domainGroup.uuid : domain.domainGroup,
    // Domain Group Nested
    ...(domainGroupNested && { domainGroupData: { ...domain.domainGroup } }),
  };
};

const toDomainRequest = (domain) => ({
  name: domain.name,
  code: domain.code,
  domainGroup: domain.domainGroupUuid,
});

export const useDomainStore = defineStore("domain", {
  state: () => ({
    domainsCount: 0,
  }),

  actions: {
    fetchPublicDomains: list({
      ModelRepo: DomainRepo,
      restListMethod: publicDomainApi.list.bind(publicDomainApi),
      onItemsInserted: ({ count }) => {
        useDomainStore().setDomainsCount(count);
      },
    }),

    fetchDomains: list({
      ModelRepo: DomainRepo,
      restListMethod: domainApi.list.bind(domainApi),
      onItemsInserted: (responseData) => {
        setDisplayedItems(DomainRepo, responseData.results);
        useDomainStore().setDomainsCount(responseData.count);
      },
    }),

    fetchDomainsWithDomainGroup: list({
      ModelRepo: DomainRepo,
      restListMethod: domainApi.listWithDomainGroup.bind(domainApi),
      handleResponseData: ({ results }) => {
        return results.map(formatDomain);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(DomainRepo, responseData.results);
        useDomainStore().setDomainsCount(responseData.count);
      },
    }),

    async fetchAllDomains(filters = {}) {
      const response = await listAll(domainApi.list.bind(domainApi), filters);

      this.setDomainsCount(response.length);

      return DomainRepo.save(response);
    },

    async createDomain(domain) {
      const response = await domainApi.create({
        domainRequest: toDomainRequest(domain),
      });

      this.addDomainsCount(1);

      return DomainRepo.save(formatDomain(response));
    },

    async updateDomain(domain) {
      const response = await domainApi.update({
        uuid: domain.uuid,
        domainRequest: toDomainRequest(domain),
      });

      return DomainRepo.save(formatDomain(response));
    },

    async deleteDomain(domain) {
      await domainApi.destroy({ uuid: domain.uuid });
      this.addDomainsCount(-1);
      DomainRepo.destroy(domain.uuid);
    },

    setDomainsCount(count) {
      this.domainsCount = count;
    },

    addDomainsCount(count) {
      this.domainsCount += count;
    },
  },

  getters: {
    getDomainsCount: (state) => state.domainsCount,
    getDomains: () => {
      return requestFactory.getters.paginateGetter(
        DomainRepo.orderBy("createdAt", "desc").withAll(),
      );
    },
    getFilteredDomains: () => {
      return DomainRepo.where("isDisplayed", true).withAll().get();
    },
    getAllDomainsUuid: () => {
      return DomainRepo.all().map((domain) => domain.uuid);
    },
    getDomainName: () => (domain_uuid) => {
      return DomainRepo.find(domain_uuid)?.name;
    },
  },
});
